import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, downloadBlobResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, FileSpreadsheet, ChevronDown, ChevronRight, AlertTriangle, Clock, Lock, Coins } from "lucide-react";
import { toast } from "sonner";
import { formatWIB } from "@/lib/datetime";

const MONTHS = [
    "", "Januari", "Februari", "Maret", "April", "Mei", "Juni",
    "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];
const num = (n) => Number(n || 0).toLocaleString("id-ID");
const cellNum = (v) => (v === null || v === undefined ? <span className="text-slate-300">N/A</span> : num(v));

const DEFAULTS = (() => {
    const d = new Date();
    let m = d.getMonth() + 1;
    let y = d.getFullYear();
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
    return { month: String(m), year: String(y), currentYear: d.getFullYear() };
})();

const JENIS = [
    { v: "1", label: "Bulanan" },
    { v: "3", label: "3 Bulan (Kuartal)" },
    { v: "6", label: "6 Bulan (Semester)" },
];

const SUMBER_BADGE = {
    otomatis: { cls: "bg-slate-100 text-slate-600 border-slate-300", text: "Otomatis" },
    koreksi_manual: { cls: "bg-blue-100 text-blue-700 border-blue-300", text: "Dikoreksi" },
    asumsi: { cls: "bg-amber-100 text-amber-700 border-amber-300", text: "Asumsi (perlu verifikasi)" },
};

function MonthHead({ m }) {
    return (
        <span className="inline-flex flex-col items-end leading-tight">
            <span>{m.label}</span>
            {m.status === "preview" && <span className="text-[9px] font-bold text-blue-600 normal-case">BELUM FINAL</span>}
            {m.status === "unavailable" && <span className="text-[9px] font-bold text-slate-400 normal-case">TAK TERSEDIA</span>}
        </span>
    );
}

function PivotTable({ title, rows, months, grandPerBulan, grandTotal }) {
    return (
        <Card className="border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-slate-50 font-bold text-slate-800 text-sm">{title}</div>
            <div className="overflow-x-auto">
                <table className="w-full compact">
                    <thead className="bg-slate-100">
                        <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                            <th className="w-10">No</th>
                            <th>Program Studi</th>
                            <th>Jenjang</th>
                            {months.map((m, i) => <th key={i} className="text-right whitespace-nowrap"><MonthHead m={m} /></th>)}
                            <th className="text-right pr-4 bg-slate-200/60">TOTAL</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r.prodi_id} className="border-t border-slate-200 hover:bg-slate-50">
                                <td className="text-slate-500">{i + 1}</td>
                                <td className="font-semibold text-slate-800">
                                    {r.nama_prodi}
                                    {r.tarif_belum_diatur && (
                                        <Badge className="ml-2 text-[9px] bg-amber-100 text-amber-700 border-amber-300" variant="outline">
                                            Tarif belum diatur
                                        </Badge>
                                    )}
                                </td>
                                <td><Badge variant="outline" className="text-[10px]">{r.jenjang}</Badge></td>
                                {r.per_bulan.map((v, j) => <td key={j} className="text-right font-mono text-xs">{cellNum(v)}</td>)}
                                <td className="text-right pr-4 font-bold font-mono text-slate-900 bg-slate-50">{num(r.total)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="border-t-2 border-slate-300 bg-slate-100 font-bold">
                            <td></td><td>GRAND TOTAL</td><td></td>
                            {grandPerBulan.map((v, j) => <td key={j} className="text-right font-mono">{cellNum(v)}</td>)}
                            <td className="text-right pr-4 font-mono">{num(grandTotal)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </Card>
    );
}

export default function LaporanUnitCost() {
    const { user } = useAuth();
    const [jenis, setJenis] = useState("1");
    const [bulan, setBulan] = useState(DEFAULTS.month);
    const [tahun, setTahun] = useState(DEFAULTS.year);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [expanded, setExpanded] = useState(null); // prodi_id yang detail-nya dibuka
    const [koreksi, setKoreksi] = useState(null); // koreksi_unit_cost_dibuka

    const years = useMemo(() => {
        const y = DEFAULTS.currentYear;
        return [y + 1, y, y - 1, y - 2, y - 3];
    }, []);

    const params = useMemo(() => ({
        bulan_mulai: Number(bulan), tahun_mulai: Number(tahun), jumlah_bulan: Number(jenis),
    }), [bulan, tahun, jenis]);

    const load = async () => {
        setLoading(true);
        setExpanded(null);
        try {
            const { data } = await api.get("/laporan/unit-cost", { params });
            setReport(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
            setReport(null);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-line */ }, [params]);
    useEffect(() => {
        api.get("/periode-config").then(({ data }) => setKoreksi(!!data.koreksi_unit_cost_dibuka)).catch(() => {});
    }, []);

    const toggleKoreksi = async (v) => {
        try {
            await api.put("/periode-config", { koreksi_unit_cost_dibuka: v });
            setKoreksi(v);
            toast.success(v ? "Jendela koreksi hari RSCM DIBUKA untuk Admin Prodi" : "Jendela koreksi DITUTUP");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const download = async () => {
        setDownloading(true);
        try {
            const res = await api.get("/laporan/unit-cost/export", { params, responseType: "blob" });
            const fallback = `aura_unit-cost_${tahun}_${String(bulan).padStart(2, "0")}${Number(jenis) > 1 ? `_${jenis}bln` : ""}.xlsx`;
            downloadBlobResponse(res, fallback);
        } catch { toast.error("Gagal export"); } finally { setDownloading(false); }
    };

    const previewMonths = report ? report.months.filter((m) => m.status === "preview") : [];
    const unavailMonths = report ? report.months.filter((m) => m.status === "unavailable") : [];
    const totalAsumsi = report ? report.months.reduce((s, m) => s + (m.asumsi || 0), 0) : 0;
    const totalBilled = report ? report.months.reduce((s, m) => s + (m.total_billed || 0), 0) : 0;
    const bulanLabel = jenis === "1" ? "Bulan" : "Bulan Awal";

    const detailFor = (prodi_id) => (report ? report.detail.filter((d) => d.prodi_id === prodi_id) : []);
    // Berhak tagih = ada entri detail (peserta Aktif & hari RSCM > 0) utk prodi itu,
    // terlepas dari apakah tarifnya sudah diatur (nominal bisa 0 kalau tarif belum
    // diisi) -- dipakai supaya baris ini TETAP tampil di "Detail per Prodi" walau
    // total Rp-nya 0 akibat tarif belum diatur (bukan disembunyikan filter total>0).
    const berhakTagih = (prodi_id) => detailFor(prodi_id).length > 0;
    const prodiRows = report ? report.iuran.filter((r) => r.total > 0 || berhakTagih(r.prodi_id)) : [];
    const prodiTarifBelumDiaturBerhakTagih = report
        ? report.iuran.filter((r) => r.tarif_belum_diatur && berhakTagih(r.prodi_id))
        : [];

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Keuangan</div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                        Laporan Unit Cost
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Tagihan unit cost PPDS per prodi. Ditagih bila status Aktif &amp; hari Stase RSCM &gt; 0
                        (1–14 hari → 50% tarif, &gt;14 hari → 100%). Bulan yang sudah final diambil dari snapshot beku.
                    </p>
                </div>
                <Button variant="outline" onClick={download} disabled={downloading || !report}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" /> {downloading ? "Menyiapkan..." : "Export Excel"}
                </Button>
            </div>

            <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-4 flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-slate-500" /> Periode
                    </span>
                    <Select value={jenis} onValueChange={setJenis}>
                        <SelectTrigger className="w-52 h-9" data-testid="uc-jenis"><SelectValue /></SelectTrigger>
                        <SelectContent>{JENIS.map((j) => <SelectItem key={j.v} value={j.v}>{j.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-xs text-slate-400 ml-1">{bulanLabel}:</span>
                    <Select value={bulan} onValueChange={setBulan}>
                        <SelectTrigger className="w-40 h-9" data-testid="uc-bulan"><SelectValue /></SelectTrigger>
                        <SelectContent>{MONTHS.slice(1).map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={tahun} onValueChange={setTahun}>
                        <SelectTrigger className="w-28 h-9" data-testid="uc-tahun"><SelectValue /></SelectTrigger>
                        <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                    {report && <span className="text-xs text-slate-500 ml-1">{report.label}</span>}
                </CardContent>
            </Card>

            {/* Kontrol jendela koreksi (Admin UFP / Super Admin) */}
            <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                    <Lock className="w-4 h-4 text-slate-500" />
                    <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-700">Jendela Koreksi Hari Stase RSCM (Admin Prodi)</div>
                        <div className="text-xs text-slate-500">
                            Bila dibuka, Admin Prodi dapat mengoreksi jumlah hari Stase RSCM peserta di form Edit
                            untuk bulan yang sedang difinalisasi. Admin UFP / Super Admin selalu bisa mengoreksi.
                        </div>
                    </div>
                    <Switch checked={!!koreksi} onCheckedChange={toggleKoreksi} data-testid="uc-koreksi-toggle" />
                    <span className={`text-xs font-bold ${koreksi ? "text-emerald-600" : "text-slate-400"}`}>
                        {koreksi ? "DIBUKA" : "DITUTUP"}
                    </span>
                </CardContent>
            </Card>

            {loading ? (
                <div className="p-10 text-slate-500">Menghitung...</div>
            ) : !report ? (
                <div className="p-10 text-slate-400">Tidak ada data.</div>
            ) : (
                <>
                    {previewMonths.length > 0 && (
                        <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 flex items-start gap-2 text-sm text-blue-800">
                            <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                            <div>
                                <span className="font-bold">Data belum final</span> untuk {previewMonths.map((m) => m.label).join(", ")} —
                                dihitung dari data live saat ini dan <span className="font-bold">masih bisa berubah</span> sampai snapshot bulanan dibuat.
                            </div>
                        </div>
                    )}
                    {unavailMonths.length > 0 && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-sm text-amber-800">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            <div>
                                <span className="font-bold">Data tidak tersedia</span> untuk {unavailMonths.map((m) => m.label).join(", ")} —
                                tidak ada snapshot bulanan (data sebelum fitur ini). Ditandai <span className="font-mono">N/A</span>, tidak dihitung.
                            </div>
                        </div>
                    )}
                    {prodiTarifBelumDiaturBerhakTagih.length > 0 && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-sm text-amber-800">
                            <Coins className="w-4 h-4 mt-0.5 shrink-0" />
                            <div>
                                <span className="font-bold">{prodiTarifBelumDiaturBerhakTagih.length} prodi</span> punya peserta
                                berhak tagih tapi <span className="font-bold">tarif belum diatur</span>:{" "}
                                {prodiTarifBelumDiaturBerhakTagih.map((r) => r.nama_prodi).join(", ")}. Nominalnya tertulis Rp 0
                                sampai UFP mengisi tarif di menu <span className="font-bold">Tarif Unit Cost</span>.
                            </div>
                        </div>
                    )}
                    {totalAsumsi > 0 && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-800">
                            <span className="font-bold">{totalAsumsi} dari {totalBilled}</span> tagihan peserta dalam periode ini
                            berdasarkan <span className="font-bold">asumsi</span> (tidak ada riwayat Stase tercatat sebelum bulannya —
                            biasanya peserta yang baru diimport/dibuat di bulan itu). Perlu diverifikasi.
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="p-4 rounded-lg bg-[#267B84] text-white">
                            {/* Teal cuma 4.94:1 dgn putih penuh -> teks tidak di-dim (white/60 gagal AA) */}
                            <div className="text-[11px] uppercase tracking-widest text-white font-semibold">Grand Total Iuran — {report.label}</div>
                            <div className="text-2xl font-extrabold mt-1">Rp {num(report.grand.iuran_total)}</div>
                        </div>
                        <div className="p-4 rounded-lg bg-white border border-slate-200">
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Tagihan Peserta (person-bulan)</div>
                            <div className="text-2xl font-extrabold text-slate-900 mt-1">{num(report.grand.peserta_total)}</div>
                        </div>
                        <div className="p-4 rounded-lg bg-white border border-slate-200">
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Berdasarkan Asumsi</div>
                            <div className="text-2xl font-extrabold text-amber-700 mt-1">{num(totalAsumsi)}</div>
                        </div>
                    </div>

                    <PivotTable title="A. Total Iuran (Rp) per Bulan per Prodi" rows={report.iuran} months={report.months}
                        grandPerBulan={report.grand.iuran_per_bulan} grandTotal={report.grand.iuran_total} />
                    <PivotTable title="B. Jumlah Peserta Didik per Bulan per Prodi (unik)" rows={report.peserta} months={report.months}
                        grandPerBulan={report.grand.peserta_per_bulan} grandTotal={report.grand.peserta_total} />

                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-2.5 border-b bg-slate-50 font-bold text-slate-800 text-sm">
                            Detail per Prodi ({report.detail.length} tagihan) — klik prodi untuk lihat per peserta
                        </div>
                        <div className="divide-y divide-slate-200">
                            {prodiRows.map((r) => {
                                const open = expanded === r.prodi_id;
                                const rows = detailFor(r.prodi_id);
                                return (
                                    <div key={r.prodi_id}>
                                        <button
                                            onClick={() => setExpanded(open ? null : r.prodi_id)}
                                            className="w-full px-4 py-2.5 flex items-center gap-2 text-sm hover:bg-slate-50 text-left"
                                            data-testid={`uc-prodi-${r.prodi_id}`}
                                        >
                                            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                            <span className="font-semibold text-slate-800 flex-1">
                                                [{r.jenjang}] {r.nama_prodi}
                                                {r.tarif_belum_diatur && (
                                                    <Badge className="ml-2 text-[9px] bg-amber-100 text-amber-700 border-amber-300" variant="outline">
                                                        Tarif belum diatur
                                                    </Badge>
                                                )}
                                            </span>
                                            <span className="text-xs text-slate-500">{rows.length} tagihan</span>
                                            <span className="font-mono font-bold text-slate-900">Rp {num(r.total)}</span>
                                        </button>
                                        {open && (
                                            <div className="overflow-x-auto bg-slate-50/50">
                                                <table className="w-full compact">
                                                    <thead>
                                                        <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                                                            <th className="pl-10">Bulan</th><th>Nama</th><th>NPM</th>
                                                            <th className="text-right">Hari RSCM</th><th className="text-right">%</th>
                                                            <th className="text-right">Nominal (Rp)</th><th className="pr-4">Sumber</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {rows.map((d, i) => {
                                                            const sb = SUMBER_BADGE[d.sumber] || SUMBER_BADGE.otomatis;
                                                            return (
                                                                <tr key={i} className="border-t border-slate-200">
                                                                    <td className="pl-10 text-xs text-slate-600 whitespace-nowrap">{d.label}</td>
                                                                    <td className="font-semibold text-slate-800">{d.nama}</td>
                                                                    <td className="font-mono text-xs">{d.npm}</td>
                                                                    <td className="text-right">{d.hari_rscm}</td>
                                                                    <td className="text-right">{d.persentase}%</td>
                                                                    <td className="text-right font-mono">{num(d.nominal_rupiah)}</td>
                                                                    <td className="pr-4">
                                                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${sb.cls}`}>
                                                                            {d.sumber === "koreksi_manual" && d.dikoreksi_oleh
                                                                                ? `Dikoreksi ${d.dikoreksi_oleh}${d.dikoreksi_pada ? ` · ${formatWIB(d.dikoreksi_pada)}` : ""}`
                                                                                : sb.text}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                        {rows.length === 0 && (
                                                            <tr><td colSpan={7} className="pl-10 py-3 text-slate-400 text-xs">Tidak ada tagihan.</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {prodiRows.length === 0 && (
                                <div className="px-4 py-8 text-center text-slate-400 text-sm">Tidak ada tagihan untuk periode ini.</div>
                            )}
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
}
